import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpen, faEye, faStar } from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";

// Poster-style book card. Visual grammar matches the catalogue
// showcase rail: cover fills the entire card, gradient overlay
// keeps title + author legible at the bottom, rating chip pinned to
// the top-right, views chip beside it. The previous variant had
// padding around a small cover with stats below — looked like a
// dashboard widget next to the catalogue's poster cards. Same data,
// same modal handoff.
const RecommendBookCard = ({
  img,
  title,
  writer,
  rating,
  ratingCount,
  views,
  description,
  totalParts,
  status,
  projectRef,
  projectId,
}) => {
  const [openModal, setOpenModal] = useState(false);
  // Track cover load failures so a 403/network-blip flips us to the
  // placeholder icon instead of the browser's broken-image glyph.
  // Reset is intentionally unwired — once a URL fails for this card
  // in this session, we don't retry.
  const [imgFailed, setImgFailed] = useState(false);
  const ratingNum = Number(rating) || 0;
  const showRating = ratingNum > 0;
  const ratingLabel = showRating ? ratingNum.toFixed(1) : "—";
  const viewsNum = Number(views) || 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpenModal(true)}
        className="group relative w-full aspect-[2/3] rounded-2xl overflow-hidden border border-border bg-surfaceAlt shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all text-left focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-label={title}
      >
        {img && !imgFailed ? (
          <img
            src={img}
            alt=""
            aria-hidden="true"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-secondText">
            <FontAwesomeIcon icon={faBookOpen} className="text-4xl" aria-hidden="true" />
          </div>
        )}

        {/* Gradient overlay so the bottom text stays readable on
            any cover. Top is fully transparent so the image isn't
            washed out at first glance. */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none"
          aria-hidden="true"
        />

        {/* Top-right meta chips: rating + views. Both gain the
            blurred-black pill so they read as overlays, not raw
            numbers stamped on the cover. */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {showRating ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 text-[11px] font-semibold">
              <FontAwesomeIcon icon={faStar} className="text-warning text-[10px]" />
              {ratingLabel}
              {ratingCount ? (
                <span className="text-white/70 tabular-nums">({ratingCount})</span>
              ) : null}
            </span>
          ) : null}
          {viewsNum ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 text-[11px] font-semibold tabular-nums">
              <FontAwesomeIcon icon={faEye} className="text-[10px]" />
              {viewsNum}
            </span>
          ) : null}
        </div>

        {/* Bottom overlay: title + author. ``line-clamp-2`` so a
            long title doesn't blow the card height; truncate the
            author handle for the same reason. */}
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <h3
            className="text-sm font-semibold leading-tight line-clamp-2 mb-0.5"
            title={title}
          >
            {title}
          </h3>
          {writer ? (
            <p className="text-[11px] text-white/80 truncate">{writer}</p>
          ) : null}
        </div>
      </button>

      {openModal && (
        <Modal
          img={img}
          title={title}
          writer={writer}
          rating={ratingNum}
          ratingCount={ratingCount}
          views={viewsNum}
          description={description}
          totalParts={totalParts}
          status={status}
          projectRef={projectRef}
          projectId={projectId}
          setOpenModal={setOpenModal}
          openModal={openModal}
        />
      )}
    </>
  );
};

export default RecommendBookCard;
